import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import ReactHtmlParser from 'html-react-parser';
import debounce from 'lodash/debounce';
import { makeStyles } from '@material-ui/core';
import {
  MDSCheckbox,
  MDSDivider,
  DropdownItem as MDSDropdownItem,
  MDSIcon,
  MDSInput,
  MDSPopover,
  MDSTypography,
} from '@marqvision/mds-v2';
import { VirtualizationWrapper, getRegExpByKeyword } from '@marqvision/shared';

const useStyles = makeStyles(({ _raw_color, color }) => ({
  content: {
    width: '400px',
    display: 'flex',
    flexDirection: 'column',
  },
  searchBox: {
    padding: '8px',
  },
  activeClientToggleBox: {
    marginTop: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    cursor: 'pointer',
  },
  clientItemListBox: {
    overflow: 'overlay',
    overflowX: 'hidden',
    maxHeight: '300px',
  },
  noResultBox: {
    padding: '64px',
    textAlign: 'center',
  },
  commonItemBox: {
    width: '100%',
    padding: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: color.bg.fill.primary.tint.normal,
    },
    '& .highlight': {
      color: `${_raw_color.yellow600} !important`,
    },
  },
  brandItemBox: {
    paddingLeft: '48px',
    backgroundColor: color.bg.surface.neutral.secondary.normal,
    '&.disabled': {
      cursor: 'default',
      backgroundColor: color.bg.surface.neutral.secondary.normal,
    },
  },
}));

type ClientItem = MDSDropdownItem<number | string> & { isActive?: boolean };
type Props<T extends ClientItem[]> = {
  value: { clientId: number; brandId?: number };
  list: T;
  isLoading: boolean;
  anchor: React.ReactElement;
  onSelect: (clientId: number, brandId?: number) => void;
};
export const MarqAiClientSelector = <T extends ClientItem[]>(props: Props<T>) => {
  const { value, list, anchor, isLoading, onSelect } = props;
  const classes = useStyles();
  const [searchText, setSearchText] = useState('');
  const [isActiveOnly, setIsActiveOnly] = useState(false);

  const handleActiveOnlyClick = (next: boolean) => {
    setIsActiveOnly(next);
  };
  const handleClientClick = (value: number, brandId?: number) => {
    onSelect(value, brandId);
  };
  const handleSearch = debounce((value: string) => {
    setSearchText(value);
  }, 300);

  const resolvedClientList = useMemo(() => {
    const targetList = isActiveOnly ? list?.filter((client) => client.isActive) : list;

    if (!searchText) return targetList;

    const isParentMatching = (item: ClientItem) => checkIsMatching(item, searchText);
    const hasMatchingChild = (item: ClientItem) =>
      !!item.children?.some((child: ClientItem) => checkIsMatching(child, searchText, true));

    const filteredList = targetList
      ?.filter((item) => {
        // 부모 항목이 검색 조건에 맞거나, 자식 항목 중 하나라도 검색 조건에 맞는 경우 해당 부모 항목 표시
        return isParentMatching(item) || hasMatchingChild(item);
      })
      .map((item) => {
        // 부모 항목이 검색 조건에 맞으면 모든 자식 항목 표시 (자식 항목 매칭 여부 상관없이)
        if (isParentMatching(item)) {
          return {
            ...item,
            // 원래 children을 그대로 유지 (필터링하지 않음)
            children: item.children,
          };
        } else {
          // 부모 항목이 검색 조건에 맞지 않고 자식 항목만 매칭되는 경우
          // 검색 조건에 맞는 자식 항목만 필터링
          return {
            ...item,
            children: item.children?.filter((child) => checkIsMatching(child, searchText, true)),
          };
        }
      });
    return filteredList || [];
  }, [list, isActiveOnly, searchText]);

  return (
    <MDSPopover anchor={anchor} width="400px" padding={0} hasDim={false} trigger="click">
      {() => {
        if (isLoading) {
          return <div />;
        }
        return (
          <div className={classes.content}>
            <div className={classes.searchBox}>
              <MDSInput size="medium" fullWidth placeholder="Search" value={searchText} onChange={handleSearch} />
              <label className={classes.activeClientToggleBox}>
                <MDSCheckbox value={isActiveOnly} onChange={handleActiveOnlyClick} size="medium" />
                <MDSTypography variant="body" size="m" weight="medium">
                  Show active clients only
                </MDSTypography>
              </label>
            </div>

            <MDSDivider />

            {resolvedClientList.length > 0 ? (
              <div className={classes.clientItemListBox}>
                {resolvedClientList.map((option) => (
                  <VirtualizationWrapper key={option.value} initHeight={45}>
                    <div className={classes.commonItemBox} onClick={() => handleClientClick(option.value)}>
                      {value.clientId == option.value && !value.brandId && (
                        <MDSIcon.Check variant="outline" color="color/content/primary/default/normal" size={24} />
                      )}
                      <div>
                        <MDSTypography
                          variant="body"
                          size="s"
                          weight={value.clientId == option.value ? 'medium' : 'regular'}
                          color={
                            value.clientId == option.value
                              ? 'color/content/primary/default/normal'
                              : 'color/content/neutral/secondary/normal'
                          }
                        >
                          <HighlightLabel searchText={searchText} label={String(option.value)} />
                        </MDSTypography>
                        <MDSTypography
                          variant="body"
                          size="m"
                          weight={value.clientId == option.value ? 'medium' : 'regular'}
                          color={
                            value.clientId == option.value
                              ? 'color/content/primary/default/normal'
                              : 'color/content/neutral/default/normal'
                          }
                        >
                          <HighlightLabel searchText={searchText} label={(option.label as string) || ''} />
                        </MDSTypography>
                      </div>
                    </div>
                    <SearchedDropItem
                      searchText={searchText}
                      option={option}
                      selectedBrandId={value.brandId}
                      onSelect={handleClientClick}
                    />
                  </VirtualizationWrapper>
                ))}
              </div>
            ) : (
              <div className={classes.noResultBox}>
                <MDSTypography variant="body" size="m" weight="regular" color="color/content/neutral/secondary/normal">
                  No result
                </MDSTypography>
              </div>
            )}
          </div>
        );
      }}
    </MDSPopover>
  );
};

// #region child components
const SearchedDropItem = ({
  option,
  searchText,
  selectedBrandId,
  onSelect,
}: {
  option: MDSDropdownItem<number | string>;
  searchText: string;
  selectedBrandId: number | undefined;
  onSelect: (clientId: number, brandId: number) => void;
}) => {
  const classes = useStyles();

  return (
    <div>
      {option.children?.map((v: MDSDropdownItem<number | string>) => (
        <VirtualizationWrapper initHeight={45} key={v.value}>
          <div
            className={clsx(classes.commonItemBox, classes.brandItemBox, v.isDisabled && 'disabled')}
            onClick={v.isDisabled ? () => {} : () => onSelect(option.value, v.value)}
          >
            {selectedBrandId == v.value && (
              <MDSIcon.Check variant="outline" color="color/content/primary/default/normal" size={24} />
            )}
            <MDSTypography
              variant="body"
              size="m"
              weight="regular"
              color={
                v.isDisabled
                  ? 'color/content/neutral/default/disabled'
                  : selectedBrandId == v.value
                  ? 'color/content/primary/default/normal'
                  : 'color/content/neutral/default/normal'
              }
            >
              <HighlightLabel searchText={searchText} label={(v.label as string) || ''} />
            </MDSTypography>
          </div>
        </VirtualizationWrapper>
      ))}
    </div>
  );
};

const HighlightLabel = ({ searchText, label }: { searchText: string; label: string }) => {
  const regex = getRegExpByKeyword(searchText);
  const getConvertedText = (text: string): string => {
    return searchText ? text?.replace(regex, '<span class="highlight">$&</span>') : text;
  };

  return ReactHtmlParser(getConvertedText(label));
};
// #endregion

// #region utils
const checkIsMatching = (item: ClientItem, searchText: string, bypassValueCheck = false) => {
  const regex = getRegExpByKeyword(searchText);
  const isLabelMatched = regex.test(item.label as string);
  const isValueMatched = bypassValueCheck ? false : regex.test(String(item.value));
  return isLabelMatched || isValueMatched;
};
// #endregion
